import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useSiteSettings } from '../hooks/useSiteSettings';
import ImagePreviewModal from '../components/ImagePreviewModal';
import './SectionDetailsPage.css';

const resolveImage = (src) => {
  if (!src) return '';
  if (src.startsWith('http') || src.startsWith('data:')) return src;
  return `${process.env.REACT_APP_API_URL || ''}${src}`;
};

const SectionDetailsPage = () => {
  const { slug } = useParams();
  const { site_name } = useSiteSettings();
  const [section, setSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [images, setImages] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    const fetchSectionData = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/sections/slug/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setSection(data);
        } else {
          setError('لم يتم العثور على هذا القسم');
        }
      } catch (error) {
        console.error('خطأ في جلب بيانات القسم:', error);
        setError('خطأ في الاتصال بالخادم');
      } finally {
        setLoading(false);
      }
    };

    fetchSectionData();
  }, [slug]);

  useEffect(() => {
    const fetchArticles = async () => {
      if (!section) return;

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/articles`);
        if (response.ok) {
          const data = await response.json();
          const sectionArticles = Array.isArray(data)
            ? data.filter(article => {
                const articleSectionId =
                  article.sectionId?.id || article.sectionId?._id || article.sectionId || null;
                return articleSectionId && section.id && articleSectionId === section.id;
              })
            : [];

          const galleryImages = sectionArticles
            .filter(article => article.image)
            .map(article => article.image);

          setImages(galleryImages);
        }
      } catch (error) {
        console.error('خطأ في جلب المقالات:', error);
      }
    };

    fetchArticles();
  }, [section]);

  if (loading) return <div style={{ textAlign: 'center' }}>جاري التحميل...</div>;
  if (error) return <div style={{ color: 'red', textAlign: 'center' }}>{error}</div>;
  if (!section) return null;

  return (
    <>
      <Helmet>
        <title>{section.name || ''} | {site_name || ''}</title>
      </Helmet>
      <div className="section-details-page" style={{ maxWidth: 1000, margin: '40px auto', padding: 0 }}>
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px #e0e0e0', padding: 32, marginBottom: 32, position: 'relative' }}>
        {section.image && (
          <img
            src={resolveImage(section.image)}
            alt={section.name}
            style={{
              width: '100%',
              maxHeight: 320,
              objectFit: 'cover',
              borderRadius: 10,
              marginBottom: 20
            }}
          />
        )}
        <h1 style={{ textAlign: 'right', color: '#1e3c72', marginBottom: 12, fontSize: 32 }}>{section.name}</h1>
        <div style={{ marginTop: 12, textAlign: 'right', color: '#444', fontSize: 18 }}>
          <b>الوصف:</b>
          <div style={{ marginTop: 4 }}>{section.description || <span style={{ color: '#aaa' }}>لا يوجد وصف</span>}</div>
        </div>
      </div>

      {images.length > 0 ? (
        <div style={{ marginTop: 0 }}>
          <h2 style={{ color: '#1e3c72', marginBottom: 24, fontSize: 26, textAlign: 'right' }}>صور القسم</h2>
          <div className="section-gallery">
            {images.map((img, index) => (
              <div
                key={index}
                className="section-gallery-item"
                role="button"
                tabIndex={0}
                onClick={() => setPreviewImage(resolveImage(img))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setPreviewImage(resolveImage(img));
                  }
                }}
              >
                <img src={resolveImage(img)} alt={`${section.name} - ${index + 1}`} loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center', color: '#888', marginTop: 40, fontSize: 20 }}>
          لا توجد صور في هذا القسم.
        </div>
      )}

      <ImagePreviewModal
        open={!!previewImage}
        image={previewImage}
        alt={section.name}
        onClose={() => setPreviewImage(null)}
      />
    </div>
    </>
  );
};

export default SectionDetailsPage;
